/**
 * Quality Tab - analysis profile + script rules + criteria weights.
 * Only controls backed by persisted project settings live here; the
 * prototype-era Score Weights / Quality Monitoring mocks were removed
 * (they were local state that the Save button silently discarded). The real
 * criteria weights (#874) are their own resource with their own Save
 * (`QualityWeightsSection`).
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Pin, AlertTriangle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { ToggleGrid } from '@/components/ui/toggle-grid';
import { Select } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getProfileDisplayName } from '@/features/prompt-builder/profileDisplay';
import { SchemeLinkBanner } from '@/features/analysis-setup';
import { QualityWeightsSection } from './QualityWeightsSection';
import type { ProjectSettingsState, ProjectSettingsActions } from '../types';

interface QualityTabProps {
  state: ProjectSettingsState;
  actions: ProjectSettingsActions;
  projectId: string;
}

export function QualityTab({ state, actions, projectId }: QualityTabProps) {
  const { t } = useTranslation(['settings', 'projects']);
  // enableScoring wired to persistent project setting (autoAdherence)
  const enableScoring = state.enableAutoAdherence;
  const setEnableScoring = (v: boolean) => actions.setEnableAutoAdherence(v);

  const pinnedProfile = state.availableProfiles.find(
    (profile) => profile.id === state.selectedPromptProfile
  );
  const pinnedProfileName = pinnedProfile
    ? getProfileDisplayName(pinnedProfile, t)
    : state.selectedPromptProfile;

  return (
    <div className="space-y-6">
      <SchemeLinkBanner step="criteria" />

      {/* --- Analysis profile + script rules side by side --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Analysis profile */}
        <div className="settings-section">
          <h3 className="text-sm font-medium text-fg mb-4">
            {t('qualityTab.promptProfile', 'Analysis profile')}
          </h3>
          <div>
            <label
              htmlFor="prompt-profile"
              className="block text-xs font-medium text-fg-secondary mb-1.5"
            >
              {t('qualityTab.activePromptProfile', 'Active analysis profile')}
            </label>
            <div className="flex items-center gap-2">
              <Select
                value={state.selectedPromptProfile}
                onValueChange={(val) => actions.setSelectedPromptProfile(val as string)}
                placeholder={t('qualityTab.selectProfile', 'Select a profile...')}
                options={state.availableProfiles.map((profile) => ({
                  value: profile.id,
                  label: getProfileDisplayName(profile, t),
                }))}
                className="flex-1"
                size="sm"
              />
              <Link
                to="/analysis-setup/categories"
                className="inline-flex items-center px-3 py-2 text-xs rounded border border-border bg-bg-surface text-fg hover:bg-bg-elevated transition-colors"
              >
                {t('qualityTab.openBuilder', 'Open Builder')}
              </Link>
            </div>
            {/* "Active" reads like "the one in use", but this profile is the
                LAST step of the resolver's cascade — it only runs when no
                binding matched. Say so where the control is. */}
            <p className="mt-1.5 text-xs text-fg-muted" data-testid="active-profile-hint">
              {t('qualityTab.activeProfileHint')}
            </p>
          </div>
          <div className="flex items-center gap-2 px-2 py-2 bg-bg-elevated rounded mt-3">
            <span className="text-xs text-fg flex-1">
              {t(
                'qualityTab.enableAutoAnalysis',
                'Automatic analysis after transcription'
              )}
            </span>
            <Switch
              checked={state.enableAutoAnalysis}
              onCheckedChange={(checked) => actions.setEnableAutoAnalysis(checked)}
              size="sm"
            />
          </div>
        </div>

        {/* Script rules */}
        <div className="settings-section">
          <h3 className="text-sm font-medium text-fg mb-4">
            {t('qualityTab.scriptAdherence', 'Script rules')}
          </h3>
          <div className="mb-3">
            <label
              htmlFor="script-select"
              className="block text-xs font-medium text-fg-secondary mb-1.5"
            >
              {t('qualityTab.activeScript', 'Active script rules')}
            </label>
            <div className="flex items-center gap-2">
              <Select
                value={state.selectedScript}
                onValueChange={(val) => actions.setSelectedScript(val as string)}
                disabled={state.loadingScripts}
                loading={state.loadingScripts}
                placeholder={t('qualityTab.noScript', 'None')}
                options={state.availableScripts.map((script) => ({
                  value: script.id,
                  label: script.name,
                }))}
                className="flex-1"
                size="sm"
              />
              <Link
                to="/analysis-setup/scripts"
                className="inline-flex items-center px-3 py-2 text-xs rounded border border-border bg-bg-surface text-fg hover:bg-bg-elevated transition-colors"
              >
                {t('qualityTab.manageScripts', 'Manage script rules')}
              </Link>
            </div>
            <p className="mt-1.5 text-xs text-fg-muted" data-testid="active-script-hint">
              {t('qualityTab.activeScriptHint')}
            </p>
          </div>
          <div className="flex items-center gap-2 px-2 py-2 bg-bg-elevated rounded mt-3">
            <span className="text-xs text-fg flex-1">
              {t('qualityTab.enableScoring', 'Enable rule-compliance scoring')}
            </span>
            <Switch
              checked={enableScoring}
              onCheckedChange={setEnableScoring}
              size="sm"
            />
          </div>
          {/* The rules above still read as assigned while this is off, and
              every other screen showed them «Active» — say here what off
              means for this project's calls (#1093). */}
          {!enableScoring && (
            <p className="mt-1.5 text-xs text-warning" data-testid="scoring-off-hint">
              {t('qualityTab.scoringOffHint')}
            </p>
          )}
        </div>
      </div>

      {/* Keyed by project: a draft (and its preview) belongs to one project and
          must not survive a switch to another while the tab stays mounted. */}
      <QualityWeightsSection key={projectId} projectId={projectId} />

      {/* --- Profile provenance --- */}
      <p className="text-xs text-fg-muted" data-testid="profile-provenance">
        {state.selectedPromptProfile && state.promptProfilePinned
          ? t('projects:qualityTab.provenancePinned', { name: pinnedProfileName })
          : t('projects:qualityTab.provenanceAuto')}
      </p>

      {/* --- Resolution order ---
          Mirrors the analyze worker's cascade (apps/workflows-api/.../analyze/
          prompt-profile.ts, steps 2-6) in the order it actually runs. Without
          it, "active profile" and "pinned profile" look like two names for the
          same thing instead of the last and the first step of one list. */}
      <div className="settings-section border border-border rounded-card p-4">
        <h3 className="text-sm font-medium text-fg mb-3">
          {t('qualityTab.resolutionOrder.title')}
        </h3>
        <ol
          className="space-y-1.5 text-xs text-fg-muted list-decimal list-inside"
          data-testid="profile-resolution-order"
        >
          <li>{t('qualityTab.resolutionOrder.pinned')}</li>
          <li>{t('qualityTab.resolutionOrder.stage')}</li>
          <li>{t('qualityTab.resolutionOrder.phase')}</li>
          <li>{t('qualityTab.resolutionOrder.category')}</li>
          <li>{t('qualityTab.resolutionOrder.project')}</li>
        </ol>
      </div>

      {/* --- Pinned analysis config --- */}
      <div className="settings-section border border-border rounded-card p-4">
        <div className="flex items-center gap-2 mb-1">
          <Pin className="h-4 w-4 text-fg-muted" />
          <h3 className="text-sm font-medium text-fg">
            {t('qualityTab.pinnedConfig.title', 'Pinned analysis config')}
          </h3>
        </div>
        <p className="text-xs text-fg-muted mb-4">
          {t(
            'qualityTab.pinnedConfig.description',
            "Pin this project's own analysis profile instead of the organization's category auto-selection."
          )}
        </p>

        <ToggleGrid className="gap-6">
          <div className="flex items-start justify-between gap-3 px-3 py-3 bg-bg-elevated rounded">
            <div className="min-w-0">
              <p className="text-sm text-fg">
                {t('qualityTab.pinnedConfig.pinProfile', 'Pin analysis profile')}
              </p>
              <p className="text-xs text-fg-muted mt-0.5">
                {state.selectedPromptProfile
                  ? t(
                      'qualityTab.pinnedConfig.pinProfileHint',
                      'Always use the profile selected above for this project.'
                    )
                  : t(
                      'qualityTab.pinnedConfig.pinProfileDisabledHint',
                      'Select an analysis profile above before pinning.'
                    )}
              </p>
            </div>
            <Switch
              checked={state.selectedPromptProfile ? state.promptProfilePinned : false}
              disabled={!state.selectedPromptProfile}
              onCheckedChange={actions.setPromptProfilePinned}
              aria-label={t('qualityTab.pinnedConfig.pinProfile', 'Pin analysis profile')}
              size="sm"
            />
          </div>
        </ToggleGrid>

        {/* Profiles only. A project's script outranks the category binding
            whether it was pinned or not (#702), which left the script pin
            deciding nothing in all four combinations — so it was removed
            rather than left in the UI promising an effect (#707). */}
        {state.selectedPromptProfile && state.promptProfilePinned && (
          <Alert variant="warning" className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t(
                'qualityTab.pinnedConfig.warning',
                'Pinned config overrides category auto-selection for this project.'
              )}
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}
